import React from "react"
import PropTypes from "prop-types"
import _ from "lodash"
import { Button, TextField, Rating, Alert, IconButton, Tooltip } from "@mui/material"
import { EditOutlined, DeleteOutline, RateReviewOutlined } from "@mui/icons-material"
import {
    useGetCourseReviewsQuery,
    useSaveMyReviewMutation,
    useDeleteMyReviewMutation
} from "../../Redux/actions/lecture.action"
import RatingSummary from "./RatingSummary"
import ReviewItem from "./ReviewItem"
import ConfirmDialog from "./ConfirmDialog"

const PAGE_SIZE = 10
const MAX_COMMENT = 2000

/** One page of reviews; pages share the RTK Query cache. */
function ReviewPage({ course_id, page }) {
    const { data } = useGetCourseReviewsQuery({
        course_id,
        offset: page * PAGE_SIZE,
        limit: PAGE_SIZE
    })
    return _.get(data, "list", []).map((review) => (
        <ReviewItem key={review.review_id} review={review} />
    ))
}

ReviewPage.propTypes = {
    course_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    page: PropTypes.number.isRequired
}

/**
 * Ratings and reviews on the course page. Everyone signed in can read them;
 * enrolled students can also write, edit or delete their own review.
 */
export default function CourseReviews({ course_id, enrolled }) {
    const [pages, setPages] = React.useState(1)
    const [editing, setEditing] = React.useState(false)
    const [form, setForm] = React.useState({ rating: 0, comment: "" })
    const [formError, setFormError] = React.useState("")
    const [confirmDelete, setConfirmDelete] = React.useState(false)

    const firstPage = useGetCourseReviewsQuery({
        course_id,
        offset: 0,
        limit: PAGE_SIZE
    })
    const summary = firstPage.data || {}
    const total = summary.total || 0
    const myReview = summary.my_review || null

    const [saveAction, saveState] = useSaveMyReviewMutation()
    const [deleteAction, deleteState] = useDeleteMyReviewMutation()
    const busy = saveState.isLoading || deleteState.isLoading

    const startEditing = () => {
        setForm({
            rating: Number(myReview?.rating) || 0,
            comment: myReview?.comment || ""
        })
        setFormError("")
        setEditing(true)
    }

    const submit = async () => {
        if (!form.rating) {
            setFormError("Pick a rating from 1 to 5 stars.")
            return
        }
        setFormError("")
        try {
            await saveAction({
                course_id,
                rating: form.rating,
                comment: form.comment.trim() || null
            }).unwrap()
            setEditing(false)
        } catch (error) {
            setFormError(
                _.get(error, "data.detail", null) || "Could not save your review."
            )
        }
    }

    const removeReview = async () => {
        try {
            await deleteAction({ course_id }).unwrap()
            setConfirmDelete(false)
            setEditing(false)
        } catch (error) {
            setConfirmDelete(false)
            setFormError(
                _.get(error, "data.detail", null) ||
                    "Could not delete your review."
            )
        }
    }

    return (
        <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 sm:p-6">
            <h2 className="text-base font-semibold text-slate-800 sm:text-lg">
                Student reviews
            </h2>

            {firstPage.isError && (
                <Alert severity="error">
                    {_.get(firstPage, "error.data.detail", null) ||
                        "Reviews could not be loaded."}
                </Alert>
            )}

            {firstPage.isSuccess && (
                <RatingSummary
                    average={summary.rating_average}
                    count={summary.rating_count}
                    distribution={summary.distribution}
                />
            )}

            {/* The student's own review */}
            {enrolled && firstPage.isSuccess && (
                <div className="rounded-xl bg-slate-50 p-3 sm:p-4">
                    {!editing && myReview && (
                        <ReviewItem
                            review={{ ...myReview, is_mine: true }}
                            actions={
                                <>
                                    <Tooltip title="Edit your review">
                                        <IconButton size="small" onClick={startEditing}>
                                            <EditOutlined fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete your review">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => setConfirmDelete(true)}
                                        >
                                            <DeleteOutline fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            }
                        />
                    )}

                    {!editing && !myReview && (
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <p className="text-sm text-slate-600">
                                How was this course? Your review helps other
                                students decide.
                            </p>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<RateReviewOutlined />}
                                onClick={startEditing}
                            >
                                Write a review
                            </Button>
                        </div>
                    )}

                    {editing && (
                        <div className="space-y-3">
                            <div>
                                <p className="text-sm font-medium text-slate-700">
                                    Your rating
                                </p>
                                <Rating
                                    size="large"
                                    value={form.rating}
                                    onChange={(event, value) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            rating: value || 0
                                        }))
                                    }
                                />
                            </div>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                size="small"
                                label="Your review (optional)"
                                value={form.comment}
                                helperText={`${form.comment.length}/${MAX_COMMENT}`}
                                onChange={(event) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        comment: event.target.value.slice(0, MAX_COMMENT)
                                    }))
                                }
                            />
                            <div className="flex justify-end gap-2">
                                <Button
                                    onClick={() => {
                                        setEditing(false)
                                        setFormError("")
                                    }}
                                    disabled={busy}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={submit}
                                    disabled={busy}
                                >
                                    {myReview ? "Save review" : "Submit review"}
                                </Button>
                            </div>
                        </div>
                    )}

                    {formError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {formError}
                        </Alert>
                    )}
                </div>
            )}

            {firstPage.isSuccess && total === 0 && (
                <p className="text-sm text-slate-500">
                    No reviews yet.
                    {enrolled ? " Be the first to share what you think." : ""}
                </p>
            )}

            <div>
                {_.range(pages).map((page) => (
                    <ReviewPage key={page} course_id={course_id} page={page} />
                ))}
            </div>

            {pages * PAGE_SIZE < total && (
                <div className="flex justify-center">
                    <Button size="small" onClick={() => setPages(pages + 1)}>
                        Load more reviews
                    </Button>
                </div>
            )}

            <ConfirmDialog
                open={confirmDelete}
                title="Delete your review?"
                message="Your rating and comment will be removed from this course."
                busy={deleteState.isLoading}
                onConfirm={removeReview}
                onCancel={() => setConfirmDelete(false)}
            />
        </section>
    )
}

CourseReviews.propTypes = {
    course_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    enrolled: PropTypes.bool
}
